import type { Station } from '@/types'
import { ref } from 'vue'
import { useApi } from '@/lib/api'
import { setMediaSessionMetadata } from '@/lib/media-session'

interface MetadataResponse {
  data: {
    StreamTitle?: string
  }
}

export interface ParsedMetadata {
  title?: string
  artist?: string
  combined?: string
}

export interface PlaylistEntry extends ParsedMetadata {
  date: Date
}

export type Playlist = Record<number, PlaylistEntry[]>

const api = useApi()
let pollTimer: ReturnType<typeof setInterval> | undefined
let activeStation: Station | undefined

export const playlist = ref<Playlist>([])

export const createStatsListener = (station: Station) => {
  stopStatsListener()
  activeStation = station

  // Fetch immediately, then poll every 15 seconds
  fetchMetadata(station)
  pollTimer = setInterval(() => fetchMetadata(station), 15000)
}

export const stopStatsListener = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = undefined
  }
  activeStation = undefined
}

// When the app returns to foreground, fetch metadata immediately
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && activeStation) {
    fetchMetadata(activeStation)
  }
})

const fetchMetadata = async (station: Station) => {
  try {
    const response = await api.ky
      .get(`stations/${station.name}/metadata/`)
      .json<MetadataResponse>()

    const metadata = response.data
    if (!metadata || !metadata.StreamTitle) {
      return
    }

    const parsedMetadata = parseStreamTitle(metadata.StreamTitle)
    if (!parsedMetadata.combined) {
      return
    }

    setMediaSessionMetadata(
      station,
      parsedMetadata.title || parsedMetadata.combined,
      parsedMetadata.artist,
    )

    if (!playlist.value[station.id]) {
      playlist.value[station.id] = []
    }

    if (
      !playlist.value[station.id].length ||
      playlist.value[station.id][0].combined !== parsedMetadata.combined
    ) {
      playlist.value[station.id] = [
        {
          date: new Date(),
          ...parsedMetadata,
        },
        ...playlist.value[station.id],
      ].slice(0, 9)
    }
  } catch {
    // Fail silently - metadata is non-critical
  }
}

const stringToSentenceCase = (input?: string): string => {
  if (!input) {
    return ''
  }

  return input.toLowerCase().replace(/\w\S*/g, function (str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  })
}

const parseStreamTitle = (streamTitle: string): ParsedMetadata => {
  const out: ParsedMetadata = {
    title: undefined,
    artist: undefined,
    combined: undefined,
  }

  const segments = streamTitle
    .split(' - ')
    .map((str) => stringToSentenceCase(str.trim()))

  if (segments.length > 1) {
    out.artist = segments[0]
    out.title = segments.slice(1).join(' - ')
    out.combined = [out.artist, out.title].filter((item) => !!item).join(' - ')
    return out
  }

  out.combined = segments[0]
  return out
}
